import { useState, useEffect } from 'react'
import {
  localeHeader,
  notFoundPath,
  productCustomisationTypes
} from '#constants'
import { useAppSelector } from '#src/state/redux-hooks'
import { bagSelector } from '#src/state/bag-state-slice'
import { translate } from '#src/common/components/translation'
import qs from 'qs'
import platformClient from '#lib/platform-client'
import { useRouter } from 'next/router'
import { productUnavailabilityRedirectUrl } from './utils'

const getProductDetails = async (sku, query, locale) => {
  return platformClient(`/products/${sku}${query}`, {
    version: 'v3',
    headers: {
      [localeHeader]: locale
    }
  })
}

export const useProductDetails = ({
  sku,
  product: _product,
  settings,
  isTransactional
}) => {
  const { push, locale } = useRouter()
  const [productData, setProductData] = useState({
    product: _product,
    isLoading: false,
    error: undefined
  })

  useEffect(() => {
    if (!isTransactional) {
      setProductData({
        ...productData,
        product: _product,
        isLoading: false,
        error: undefined
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransactional])
  const bag = useAppSelector(bagSelector)

  const { isLoading, orderDetails } = bag || {}
  const {
    pickupTimeStart,
    pickupTimeEnd,
    fulfillingShopId: shopId
  } = orderDetails || {}

  const handleErrors = error => {
    if (error.response?.status === 404) {
      setProductData({
        error: undefined,
        product: null,
        isLoading: false
      })
      push(notFoundPath)
    } else {
      setProductData({
        ...productData,
        product: null,
        isLoading: false,
        error: error.response
      })
    }
  }

  const handleProductVisibility = () => {
    const redirectUrl = productUnavailabilityRedirectUrl(settings, locale)
    setProductData({
      error: undefined,
      product: null,
      isLoading: false
    })
    push(redirectUrl)
  }

  useEffect(() => {
    async function fetchProductDetails() {
      if (!isLoading && pickupTimeStart && pickupTimeEnd && shopId) {
        const query = {
          pickupTimeStart,
          pickupTimeEnd,
          shopId
        }
        const queryString = `?${qs.stringify(query)}` || ''
        try {
          setProductData({ ...productData, isLoading: true })
          const result = await getProductDetails(sku, queryString, locale)
          if (result) {
            if (result.visibleInMenu === false) {
              handleProductVisibility(result)
            } else {
              setProductData({
                error: undefined,
                product: updateCustomisationOptions(result, locale),
                isLoading: false
              })
            }
          }
        } catch (error) {
          handleErrors(error)
        }
      }
    }
    if (bag && isTransactional) {
      setProductData({ ...productData, isLoading: isLoading })
      fetchProductDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, pickupTimeStart, pickupTimeEnd, shopId, sku, isTransactional])
  return productData
}

const updateCustomisationOptions = (product, locale) => {
  const customisationOptions = []
  product.customisationOptions?.forEach(customisationOption => {
    if (customisationOption.type === productCustomisationTypes.TOPPERS) {
      customisationOptions.unshift({
        ...customisationOption,
        options: [
          {
            isDefault: true,
            label: translate('product.toppers.option.default-label', locale)
              .value,
            isSelected: true
          },
          ...customisationOption.options.map(option => ({
            ...option,
            isSelected: false
          }))
        ],
        subHeading: translate('product.toppers.sub-heading', locale).value
      })
    } else {
      customisationOptions.push({
        ...customisationOption,
        options: customisationOption.options.map(option => ({
          ...option,
          isSelected: false
        }))
      })
    }
  })

  return {
    ...product,
    customisationOptions
  }
}
